
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Head from 'next/head';
import type { ProductWithPrice } from 'types';
import clsxm from '@/lib/clsxm';
import { getProductsWithPricesInArray } from '@/data/products';
import { tone } from '@/components/agents/primitives';
import { LANDING_PATH, type PlanPrice, useReceptionistT, } from '@/components/ai-receptionist/kit';
import ReceptionistCompare from '@/components/ai-receptionist/ReceptionistCompare';
import ReceptionistCost from '@/components/ai-receptionist/ReceptionistCost';
import ReceptionistFaq from '@/components/ai-receptionist/ReceptionistFaq';
import ReceptionistFinalCta from '@/components/ai-receptionist/ReceptionistFinalCta';
import ReceptionistHero from '@/components/ai-receptionist/ReceptionistHero';
import ReceptionistIndustries from '@/components/ai-receptionist/ReceptionistIndustries';
import ReceptionistJobs from '@/components/ai-receptionist/ReceptionistJobs';
import ReceptionistLogos from '@/components/ai-receptionist/ReceptionistLogos';
import ReceptionistSetup from '@/components/ai-receptionist/ReceptionistSetup';
import ReceptionistVoice from '@/components/ai-receptionist/ReceptionistVoice';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';
const SITE = 'https://easy-peasy.ai';
/*
 * The AI receptionist landing page, for "AI receptionist" and "AI answering
 * service" searches. Story: every call answered in your name (hero), the
 * calls it takes off your hands, how a call feels, how a Twilio number gets
 * connected, who it's for, an honest comparison with people, what it costs,
 * then questions. Phone calls are the Agents product's Twilio channel.
 */
export default function AiReceptionist({ starter }: {
    starter: PlanPrice;
}) {
    const { t, lang } = useReceptionistT();
    const prefix = lang === 'en' ? '' : `/${lang}`;
    const breadcrumbs = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: t('AI Agents'),
                item: `${SITE}${prefix}/chatbots`,
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: t('AI receptionist'),
                item: `${SITE}${prefix}${LANDING_PATH}`,
            },
        ],
    };
    return (
    // `relative` makes <main> the containing block of the footer's sr-only
    // heading, so overflow-x-clip catches the 1px it pokes out in RTL.
    <main className={clsxm('relative overflow-x-clip', tone.page)} dir={lang === 'ar' ? 'rtl' : undefined}>
      <Seo templateTitle={t('AI Receptionist: Answer Every Business Call 24/7')} description={t('AI receptionist and answering service for small business. It picks up every call 24/7, answers from your own information, books appointments and takes messages.')} image={`${SITE}/api/og-template?title=${encodeURIComponent(t('An AI receptionist that answers every call'))}`} structuredData={false}/>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbs).replace(/</g, '\\u003c'),
        }}/>
      </Head>

      <ReceptionistHero />
      <ReceptionistLogos />
      <ReceptionistJobs />
      <ReceptionistVoice />
      <ReceptionistSetup />
      <ReceptionistIndustries />
      <ReceptionistCompare price={starter}/>
      <ReceptionistCost price={starter}/>
      <ReceptionistFaq price={starter}/>
      <ReceptionistFinalCta />
      <Footer />
    </main>);
}
// Starter, the cheapest plan /chatbots sells; every paid plan takes calls.
const STARTER_PRODUCT = 'prod_MakjxDVwGdvoVK';
async function getStaticProps() {
    let starter: PlanPrice = null;
    try {
        const [product] = (await getProductsWithPricesInArray([
            STARTER_PRODUCT,
        ])) as ProductWithPrice[];
        // Prices come sorted cheapest first, the way /chatbots picks them.
        const monthly = product?.prices?.find((price) => price.interval === 'month');
        const yearly = product?.prices?.find((price) => price.interval === 'year');
        if (monthly?.unit_amount) {
            starter = {
                cents: monthly.unit_amount,
                yearlyCents: yearly?.unit_amount
                    ? Math.round(yearly.unit_amount / 12)
                    : null,
                currency: monthly.currency ?? 'usd',
            };
        }
    }
    catch (error) {
        // The page still renders; the copy that quotes the price drops it until
        // the next regeneration.
        console.error('[ai-receptionist] could not load the Starter price', error);
    }
    return {
        props: { starter },
        // Prices come from Stripe products: pick up changes within the hour.
        revalidate: 3600,
    };
}

    async function __Next_Translate__getStaticProps__1a15d8fc273__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-receptionist',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a15d8fc273__ as getStaticProps }
  